/**
 * Helper function to find a wallet address with sufficient balance for a transaction
 * @param utxosHex - Array of UTXO hex strings from wallet
 * @param requiredLovelace - Required amount in lovelace
 * @param multiplier - Safety multiplier for fees (default 1.5x)
 * @returns Promise containing the address with sufficient balance
 */
export async function findAddressWithBalance(
  utxosHex: string[],
  requiredLovelace: number,
  multiplier: number = 1.5
): Promise<string> {
  if (!utxosHex || utxosHex.length === 0) {
    throw new Error("No UTXOs found in wallet");
  }

  // Convert hex string to Uint8Array
  const hexToBytes = (hex: string) => {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < hex.length; i += 2) {
      bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
    }
    return bytes;
  };

  // Parse UTXOs and group by address to find one with enough balance
  const { TransactionUnspentOutput } = await import(
    "@emurgo/cardano-serialization-lib-asmjs"
  );

  const addressBalances = new Map<string, number>();

  for (const utxoHex of utxosHex) {
    const utxo = TransactionUnspentOutput.from_bytes(hexToBytes(utxoHex));
    const address = utxo.output().address().to_bech32();
    const lovelace = Number(utxo.output().amount().coin().to_str());

    const current = addressBalances.get(address) || 0;
    addressBalances.set(address, current + lovelace);
  }

  // Find first address with enough balance (with safety multiplier for fees)
  for (const [address, balance] of addressBalances.entries()) {
    if (balance >= requiredLovelace * multiplier) {
      console.log(
        `Found address with ${balance / 1_000_000} ADA:`,
        address
      );
      return address;
    }
  }

  throw new Error(
    `No address found with enough balance. Need at least ${
      (requiredLovelace * multiplier) / 1_000_000
    } ADA (including fees)`
  );
}

/**
 * Helper function to convert hex string to Uint8Array
 */
export function hexToBytes(hex: string): Uint8Array {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return bytes;
}
